// AmsRules.tsx — the hardship policy, and the legal ladder, as the system actually applies them.
//
// This screen exists so the policy is inspectable by the people bound by it. `blocksDemand` is not
// a description of intent — it is the flag the approvals route checks before it will record an
// approval, so what is printed here is what the server does.
import { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { api, ApiError, type HardshipRule } from '../../lib/api'
import { money } from '../../lib/format'
import DataTable from '../DataTable'

type Stage = { key: string; name: string; order: number; approverRole: string
               feeCents: number; lawFirm: boolean }

const TIER: Record<string, string> = {
  plan_conditional: 'Plan-conditional', deferral: 'Deferral', hard_stop: 'Hard stop',
}

export default function AmsRules() {
  const { me } = useAuth()
  const org = me?.org?.id
  const [d, setD] = useState<{ rules: HardshipRule[]; stages: Stage[] } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!org) return
    api.ams.rules(org).then(setD)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)))
  }, [org])

  if (error) return <div className="page-state page-state-error">Could not load: {error}</div>
  if (!d) return <div className="page-state">Loading…</div>

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>Hardship rules</h2>
          <p className="muted">
            Applied to every Category 1 case, so each decision is consistent and can be audited.
            A rule marked “blocks demand” is enforced by the server, not by this screen.
          </p>
        </div>
      </header>

      <section className="card">
        {/* Six columns of which three are sentences — the worst possible shape for a phone. The
            driver is the headline, the code and tier are tokens beside it, and the prose gets its
            own lines. */}
        <DataTable
          rows={d.rules}
          rowKey={r => r.code}
          empty="No hardship rules configured."
          columns={[
            { key: 'driver', header: 'Driver', role: 'primary', cell: r => r.driver },
            { key: 'code', header: 'Code', role: 'trailing',
              cell: r => <span className="tag c1">{r.code}</span> },
            { key: 'tier', header: 'Tier',
              cell: r => (
                <>
                  <span className={`tag ${r.tier === 'hard_stop' ? 'stop' : r.tier === 'deferral' ? 'blue' : 'warn'}`}>
                    {TIER[r.tier]}
                  </span>
                  {r.blocksDemand && <div className="muted xs">No demand letter</div>}
                </>
              ) },
            { key: 'treatment', header: 'What the system does', wide: true, cell: r => r.treatment },
            { key: 'offer', header: 'Offer', wide: true,
              cell: r => <span className="muted sm">{r.offer}</span> },
            { key: 'evidence', header: 'Evidence and expiry', wide: true,
              cell: r => (
                <span className="muted sm">
                  {r.evidence}
                  {r.reviewMonths && <div className="xs">Reviewed every {r.reviewMonths} months</div>}
                </span>
              ) },
          ]}
        />
      </section>

      <section className="card">
        <h3>Legal ladder</h3>
        <p className="muted sm">
          Configuration, not code: three demands by default, and an officer may go to summons
          earlier with a recorded reason. Changing the ladder does not need a release.
        </p>
        <DataTable
          rows={d.stages}
          rowKey={s => s.key}
          empty="No legal ladder configured."
          columns={[
            { key: 'name', header: 'Stage', role: 'primary', cell: s => s.name },
            { key: 'order', header: '#', role: 'trailing',
              cell: s => <span className="muted">{s.order}</span> },
            { key: 'approver', header: 'Approved by', cell: s => s.approverRole.replace(/_/g, ' ') },
            { key: 'fee', header: 'Fee', num: true, cell: s => (s.feeCents ? money(s.feeCents) : '—') },
            { key: 'firm', header: 'Law firm',
              cell: s => <span className="muted">{s.lawFirm ? 'notified' : 'no'}</span> },
          ]}
        />
      </section>
    </div>
  )
}
